import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BookingForm from "@/components/BookingForm";
import { useAuth } from "@/lib/auth";

/**
 * Step 3 of the new-booking funnel.
 * Receives slot + Doc-AI prefill from /bookings/new/docs via location.state.
 * Opens BookingForm pre-filled. On save → navigate to /bookings.
 * On dialog close → back to slot picker.
 */
export default function NewBookingEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;
  const [open, setOpen] = useState(true);
  // After a successful save we navigate the user back to wherever they
  // started from; the BookingForm then calls onOpenChange(false) to close
  // the modal, which used to fire a second navigate("/bookings/new") and
  // wipe the original landing. This ref tells handleOpenChange to skip
  // that re-navigate when the close came from a save.
  const savedRef = useRef(false);
  const { assignments = [], hasRole } = useAuth();
  const bookerCompanyId = assignments.find(
    (a) => a.role === "approved_booker" && a.approved_booker_id && a.client_id === state?.site?.client_id,
  )?.approved_booker_id;
  const isBookerOnly = hasRole(["approved_booker"])
    && !hasRole(["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"]);

  // Guard: refresh / direct nav drops state → kick back to step 1.
  useEffect(() => {
    if (!state || !state.site || !state.bay) {
      toast.error("Booking context lost — start from the slot picker.");
      navigate("/bookings/new", { replace: true });
    }
  }, [state, navigate]);

  if (!state) return null;

  // Compose the prefill BookingForm understands (additive — only fills blanks)
  const prefill = {
    scheduledStart: state.scheduledStart,
    scheduledEnd:   state.scheduledEnd,
    clientId:       state.clientId,
    driver_name:    state.prefill?.driver_name || "",
    driver_phone:   state.prefill?.driver_phone || "",
    ...(state.prefill || {}),
    // Carrier users: stamp their company so colleagues + auto-approve rules see it.
    approved_booker_id: bookerCompanyId || state.prefill?.approved_booker_id || "",
  };

  const handleSaved = (saved) => {
    toast.success(`Booking ${saved?.reference_number || ""} created`);
    savedRef.current = true;
    setOpen(false);
    // If the user kicked off the funnel from a specific page (Bay View,
    // Site Manager, Dock Manager …), drop them back there so the new
    // booking appears in context. Otherwise default to Dock Manager.
    let returnTo = null;
    try {
      returnTo = sessionStorage.getItem("lbs.bookingReturnTo");
      sessionStorage.removeItem("lbs.bookingReturnTo");
    } catch (_e) { /* sessionStorage disabled */ }
    if (returnTo && !returnTo.startsWith("/bookings/new")) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (isBookerOnly) {
      navigate("/booker", { replace: true });
      return;
    }
    const anchor = state.scheduledStart || new Date().toISOString();
    navigate(`/dock?site=${state.site.id}&anchor=${encodeURIComponent(anchor)}`, { replace: true });
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    // Skip the back-to-picker navigate when the close was driven by a
    // successful save (handleSaved already routed the user where they
    // came from).
    if (!nextOpen && !savedRef.current) {
      navigate("/bookings/new", { replace: true });
    }
  };

  return (
    <div data-testid="new-booking-edit">
      <BookingForm
        open={open}
        onOpenChange={handleOpenChange}
        booking={null}
        defaultSiteId={state.site.id}
        defaultBayId={state.bay.id}
        prefill={prefill}
        onSaved={handleSaved}
      />
    </div>
  );
}
